/*
  Vortex lead journey.

  Remembers, in this visitor's own browser, where they first came from and what they looked at.
  When they send the team kit form (or the contact form) it adds a short lead summary to the
  email Shopify sends you: source, visits, pages, and an intent score with the reasons for it.

  Nothing leaves the browser until the visitor submits a form themselves. Storage only happens
  when Shopify's customer privacy settings allow analytics for this visitor.
*/
(() => {
  const KEY = 'vx_journey_v1';
  const SESSION_GAP = 30 * 60 * 1000; // 30 minutes of quiet starts a new visit
  const MAX_PAGES = 30;

  // Pages that mean "team" interest: the team kit page, club stores and team deposits.
  const TEAM_PATHS = [/^\/pages\/team-orders/, /^\/collections\/custom-team-deposits/, /^\/collections\/maharlikans/, /^\/pages\/maharlikans/];
  const isTeamPath = (path) => TEAM_PATHS.some((re) => re.test(path));

  /* ---------- consent ---------- */
  const consent = () =>
    new Promise((resolve) => {
      const check = () => {
        const cp = window.Shopify && window.Shopify.customerPrivacy;
        if (cp && typeof cp.analyticsProcessingAllowed === 'function') return resolve(cp.analyticsProcessingAllowed());
        return resolve(true); // no consent banner configured for this store or region
      };
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        try {
          window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => (err ? resolve(true) : check()));
          return;
        } catch (e) {
          /* fall through */
        }
      }
      check();
    });

  /* ---------- storage ---------- */
  const load = () => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || null;
    } catch (e) {
      return null;
    }
  };
  const save = (data) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(data));
    } catch (e) {
      /* storage blocked: the summary will just be thinner */
    }
  };

  /* ---------- where did they come from ---------- */
  const SEARCH = /(^|\.)(google|bing|duckduckgo|yahoo|ecosia|brave|baidu|yandex)\./;
  const classify = () => {
    const url = new URL(window.location.href);
    const p = url.searchParams;
    const ua = navigator.userAgent || '';
    let ref = '';
    try {
      ref = document.referrer ? new URL(document.referrer).hostname.replace(/^www\./, '') : '';
    } catch (e) {
      ref = '';
    }
    const ownHost = window.location.hostname.replace(/^www\./, '');
    const internal = ref && (ref === ownHost || ref.endsWith('vortexrugby.com') || ref.endsWith('myshopify.com'));

    if (p.get('utm_source')) {
      const bits = [p.get('utm_source'), p.get('utm_medium'), p.get('utm_campaign')].filter(Boolean);
      return { channel: tidy(p.get('utm_source')), detail: bits.join(' / '), paid: /cpc|paid|ad/i.test(p.get('utm_medium') || '') };
    }
    if (p.get('gclid') || p.get('gbraid') || p.get('wbraid')) return { channel: 'Google Ads', detail: 'gclid', paid: true };
    if (p.get('fbclid') && /instagram/i.test(ua)) return { channel: 'Instagram', detail: 'Instagram app link', paid: false };
    if (p.get('fbclid')) return { channel: 'Facebook', detail: 'fbclid', paid: false };
    if (p.get('ttclid')) return { channel: 'TikTok', detail: 'ttclid', paid: true };
    if (/Instagram/i.test(ua) && !internal) return { channel: 'Instagram', detail: 'Instagram in-app browser', paid: false };
    if (/FBAN|FBAV/i.test(ua) && !internal) return { channel: 'Facebook', detail: 'Facebook in-app browser', paid: false };
    if (/musical_ly|BytedanceWebview|TikTok/i.test(ua) && !internal) return { channel: 'TikTok', detail: 'TikTok in-app browser', paid: false };
    if (internal) return null; // moving around our own site, not a new source
    if (!ref) return { channel: 'Direct', detail: 'Typed the address, a bookmark, or a link from a text or app', paid: false };
    if (/instagram\.com$/.test(ref)) return { channel: 'Instagram', detail: ref, paid: false };
    if (/facebook\.com$|fb\.me$/.test(ref)) return { channel: 'Facebook', detail: ref, paid: false };
    if (/tiktok\.com$/.test(ref)) return { channel: 'TikTok', detail: ref, paid: false };
    if (/(^|\.)t\.co$|twitter\.com$|x\.com$/.test(ref)) return { channel: 'X / Twitter', detail: ref, paid: false };
    if (/youtube\.com$|youtu\.be$/.test(ref)) return { channel: 'YouTube', detail: ref, paid: false };
    if (/mail\.|outlook\.|hubspot|klaviyo/.test(ref)) return { channel: 'Email', detail: ref, paid: false };
    if (SEARCH.test(ref)) return { channel: 'Search', detail: ref, paid: false };
    return { channel: 'Another website', detail: ref, paid: false };
  };
  const tidy = (s) => {
    const v = String(s).toLowerCase();
    if (v.startsWith('ig') || v.includes('instagram')) return 'Instagram';
    if (v.includes('facebook') || v === 'fb') return 'Facebook';
    if (v.includes('tiktok')) return 'TikTok';
    if (v.includes('google')) return 'Google';
    if (v.includes('hubspot') || v.includes('email') || v.includes('klaviyo')) return 'Email';
    return s;
  };

  /* ---------- record this page ---------- */
  const pageType = () => {
    const m = document.body.className.match(/template-([a-z0-9_-]+)/);
    return m ? m[1] : 'page';
  };

  const record = (data) => {
    const now = Date.now();
    const path = window.location.pathname;
    const touch = classify();

    if (!data) {
      data = { firstSeen: now, first: touch || { channel: 'Direct', detail: '', paid: false }, landing: path, visits: 0, pages: 0, teamViews: 0, teamVisitSessions: [], products: [], history: [], session: null };
    }

    // A new visit starts after 30 quiet minutes, or when they arrive again from a named source
    // (a new Instagram tap, an email, a search). Typing the address mid-visit never overrides
    // a source we already know, the same rule Google Analytics uses.
    const named = touch && touch.channel !== 'Direct';
    const differs = named && data.session && (touch.channel !== data.session.source.channel || touch.detail !== data.session.source.detail);
    const newSession = !data.session || now - data.session.last > SESSION_GAP || differs;
    if (newSession) {
      data.visits += 1;
      data.session = { id: data.visits, start: now, last: now, source: touch || { channel: 'Direct', detail: '', paid: false }, landing: path, pages: 0, teamScroll: 0 };
    }

    data.session.last = now;

    // a refresh or back/forward is not a new page view
    let navType = 'navigate';
    try {
      const nav = performance.getEntriesByType('navigation')[0];
      if (nav) navType = nav.type;
    } catch (e) {
      /* older browsers */
    }
    const last = data.history[data.history.length - 1];
    if ((navType === 'reload' || navType === 'back_forward') && last && last.p === path && !newSession) return data;

    data.session.pages += 1;
    data.pages += 1;

    if (isTeamPath(path)) {
      data.teamViews += 1;
      if (!data.teamVisitSessions.includes(data.session.id)) data.teamVisitSessions.push(data.session.id);
    }

    if (pageType() === 'product') {
      const title = (document.querySelector('.product__title') || {}).textContent;
      if (title && !data.products.includes(title.trim())) data.products.push(title.trim());
      data.products = data.products.slice(-10);
    }

    data.history.push({ t: now, p: path, v: data.session.id });
    data.history = data.history.slice(-MAX_PAGES);
    return data;
  };

  /* ---------- intent score ---------- */
  const DECISION_ROLES = ['President', 'Kit manager', 'Kit or equipment manager', 'Captain', 'Coach'];

  const score = (data, form) => {
    const reasons = [];
    let pts = 0;
    const add = (n, why) => { pts += n; reasons.push(`+${n} ${why}`); };

    if (data) {
      if (data.visits >= 3) add(2, `came back ${data.visits} times`);
      else if (data.visits === 2) add(1, 'second visit');
      if (data.pages >= 10) add(2, `${data.pages} pages viewed`);
      else if (data.pages >= 5) add(1, `${data.pages} pages viewed`);
      if (data.teamVisitSessions.length >= 2) add(1, 'looked at team kit on more than one visit');
      if (data.session && data.session.teamScroll >= 60) add(1, 'read most of the team kit page');
      if (data.products.length >= 2) add(1, 'browsed products first');
      const mins = data.session ? (Date.now() - data.session.start) / 60000 : 0;
      if (mins >= 3) add(1, `${Math.round(mins)} min on site this visit`);
      if (data.session && isTeamPath(data.session.landing)) add(1, 'went straight to the team page');
      const ch = data.first && data.first.channel;
      if (['Instagram', 'Email', 'Another website', 'Facebook', 'TikTok'].includes(ch)) add(1, `warm source (${ch})`);
      else if (ch === 'Direct') add(1, 'knew the address (direct)');
    }

    if (form) {
      const val = (name) => {
        const el = form.querySelector(`[name="contact[${name}]"]:checked`) || form.querySelector(`[name="contact[${name}]"]:not([type=radio]):not([type=checkbox])`);
        return el ? el.value.trim() : '';
      };
      const needBy = val('Need by');
      if (needBy) {
        const days = Math.round((new Date(needBy) - Date.now()) / 86400000);
        if (days >= 0 && days <= 120) add(2, `needs it in ${days} days`);
        else if (days > 120) add(1, 'planning ahead');
      }
      const players = val('Players');
      if (/31|46|60/.test(players)) add(1, `${players} players`);
      const role = val('Role');
      if (DECISION_ROLES.includes(role)) add(1, `${role.toLowerCase()} (decision-maker)`);
      const needs = form.querySelectorAll('input[name^="contact[Needs:"]:checked').length;
      if (needs >= 2) add(1, `wants ${needs} items`);
      if (val('body').length > 20) add(1, 'wrote extra detail');
      if (val('phone')) add(1, 'left a phone number');
    }

    const label = pts >= 9 ? 'HIGH' : pts >= 5 ? 'MEDIUM' : 'LOW';
    return { pts, label, reasons };
  };

  /* ---------- helpers for the summary ---------- */
  const ago = (ts) => {
    const d = Math.round((Date.now() - ts) / 86400000);
    if (d <= 0) return 'today';
    if (d === 1) return 'yesterday';
    return `${d} days ago`;
  };
  const src = (s) => (s ? (s.detail && s.detail !== s.channel ? `${s.channel} (${s.detail})` : s.channel) : 'Unknown');

  const summarise = (data, form) => {
    const s = score(data, form);
    const out = {
      'Lead intent': `${s.label} (${s.pts} pts)`,
      'Why': s.reasons.join('; ') || 'No browsing history (first page, or tracking not allowed)',
    };
    if (data) {
      const recent = data.history.filter((h) => h.v === data.session.id).map((h) => h.p);
      out['Source, first visit'] = src(data.first);
      out['Source, this visit'] = src(data.session.source);
      out['First seen'] = `${new Date(data.firstSeen).toLocaleDateString()} (${ago(data.firstSeen)})`;
      out['Visits'] = String(data.visits);
      out['Pages viewed'] = `${data.pages} total, ${data.session.pages} this visit`;
      out['Landing page, first visit'] = data.landing;
      // repeats of the same page in a row read as one step: /pages/team-orders ×3
      const steps = [];
      recent.forEach((p) => {
        const lastStep = steps[steps.length - 1];
        if (lastStep && lastStep.p === p) lastStep.n += 1;
        else steps.push({ p, n: 1 });
      });
      out['Path this visit'] = steps.map((st) => (st.n > 1 ? `${st.p} ×${st.n}` : st.p)).join(' > ');
      if (data.products.length) out['Products viewed'] = data.products.join(', ');
      out['Team pages viewed'] = String(data.teamViews);
    } else {
      out['Source, this visit'] = src(classify());
    }
    return { fields: out, score: s };
  };

  const stamp = (form, fields) => {
    form.querySelectorAll('[data-journey-field]').forEach((el) => el.remove());
    const anchor = form.firstElementChild;
    Object.entries(fields).forEach(([k, v]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `contact[${k}]`;
      input.value = v;
      input.setAttribute('data-journey-field', '');
      form.insertBefore(input, anchor);
    });
  };

  /* ---------- run ---------- */
  let data = null;
  // how far down the team page they have read, as a percent; kept as the max for this visit
  const measureTeamRead = () => {
    if (!data || !data.session) return;
    const h = document.documentElement.scrollHeight - window.innerHeight;
    const pct = h > 0 ? Math.min(100, Math.round((window.scrollY / h) * 100)) : 100;
    if (pct > (data.session.teamScroll || 0)) {
      data.session.teamScroll = pct;
      save(data);
    }
  };

  consent().then((allowed) => {
    if (allowed) {
      data = record(load());
      save(data);

      // how far down the team page they read
      if (isTeamPath(window.location.pathname)) {
        window.addEventListener('scroll', measureTeamRead, { passive: true });
        window.addEventListener('pagehide', measureTeamRead);
        document.addEventListener('visibilitychange', measureTeamRead);
      }
    }

    document.addEventListener('submit', (e) => {
      const form = e.target;
      const isKit = form.matches('[data-kit-form]');
      if (!isKit && form.id !== 'ContactForm') return;
      if (allowed && isTeamPath(window.location.pathname)) measureTeamRead();
      const fresh = allowed ? load() || data : null;
      const { fields, score: s } = summarise(fresh, isKit ? form : null);
      if (!allowed) fields['Tracking'] = 'Visitor has not allowed analytics, so only this visit is known';
      stamp(form, fields);

      // also raise a custom event, so a pixel (GA4, Meta) set up in Shopify can count leads
      try {
        if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
          window.Shopify.analytics.publish(isKit ? 'vortex_team_kit_submitted' : 'vortex_contact_submitted', {
            intent: s.label,
            score: s.pts,
            source: fields['Source, first visit'] || fields['Source, this visit'],
          });
        }
      } catch (err) {
        /* analytics is optional */
      }
    }, true);
  });

  // exposed for checking in the browser console: vortexJourney.peek()
  window.vortexJourney = { classify, peek: () => load(), score: (form) => summarise(load(), form || document.querySelector('[data-kit-form]')) };
})();
